import React, { useEffect, useState } from "react";
import { PageHeader } from "../../components/Shell";
import SchoolLogo, { bumpLogoVersion } from "../../components/SchoolLogo";
import { api, API_URL, tokenStore, formatApiError } from "../../lib/api";
import { toast } from "sonner";
import { Upload, Trash2 } from "lucide-react";

export default function Branding() {
  const [info, setInfo] = useState(null);
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(null);

  const refresh = async () => {
    try {
      // We can't read DB doc directly; just probe the file endpoint.
      const res = await fetch(`${API_URL}/branding/logo?v=${Date.now()}`);
      if (res.ok) {
        setInfo({ uploaded: true, size: Number(res.headers.get("content-length") || 0) });
      } else {
        setInfo({ uploaded: false });
      }
    } catch {
      setInfo({ uploaded: false });
    }
  };

  useEffect(() => {
    refresh();
  }, []);

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 4 * 1024 * 1024) {
      toast.error("File too large (max 4MB)");
      return;
    }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await fetch(`${API_URL}/branding/logo`, {
        method: "POST",
        headers: { Authorization: `Bearer ${tokenStore.get()}` },
        body: fd,
      });
      if (!res.ok) {
        const j = await res.json().catch(() => ({}));
        throw new Error(j.detail || `HTTP ${res.status}`);
      }
      toast.success("Logo updated");
      bumpLogoVersion();
      await refresh();
      setPreview(null);
    } catch (err) {
      toast.error(err.message || "Upload failed");
    } finally {
      setBusy(false);
      e.target.value = "";
    }
  };

  const onPick = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setPreview(URL.createObjectURL(file));
    // Auto-upload right after pick
    upload(e);
  };

  const remove = async () => {
    if (!window.confirm("Remove the uploaded school logo? The default mark will be used.")) return;
    try {
      await api.delete("/branding/logo");
      toast.success("Logo removed");
      bumpLogoVersion();
      setPreview(null);
      refresh();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  return (
    <div data-testid="branding-page">
      <PageHeader eyebrow="Super Admin" title="Branding" />

      <div className="grid md:grid-cols-2 gap-6">
        <div className="border border-[#D2D8D4] rounded-md bg-white p-6">
          <div className="text-[10px] font-bold uppercase tracking-[0.22em] text-[#05126E]">
            Current school logo
          </div>
          <div className="mt-4 grid place-items-center bg-[#F4F6F5] border border-[#E3E6E4] rounded-md p-8 min-h-[260px]">
            {preview ? (
              <img
                src={preview}
                alt="Preview"
                className="max-h-[200px] object-contain"
                data-testid="logo-preview"
              />
            ) : (
              <SchoolLogo size={180} />
            )}
          </div>
          <div className="text-xs text-[#5a6663] mt-3">
            {info?.uploaded
              ? "A custom logo is currently uploaded. Replace it below."
              : "No custom logo uploaded yet — the default GANAP INC. shield is shown."}
          </div>
        </div>

        <div className="border border-[#D2D8D4] rounded-md bg-white p-6">
          <div className="text-[10px] font-bold uppercase tracking-[0.22em] text-[#05126E]">
            Upload / replace
          </div>
          <h2 className="text-xl font-extrabold text-[#0f1a14] mt-1">
            Choose an image
          </h2>
          <p className="text-sm text-[#5a6663] mt-2">
            PNG, JPG, WebP, or SVG. Max 4MB. A square crop works best — the
            logo appears in the header, login screen, QR ID cards, and
            unauthorized-scan page.
          </p>

          <label className="mt-5 inline-flex items-center gap-2 px-5 py-3 rounded-md bg-[#05126E] text-white font-semibold hover:bg-[#030B45] cursor-pointer transition-colors">
            <Upload className="w-4 h-4" />
            {busy ? "Uploading…" : "Choose file"}
            <input
              type="file"
              accept="image/png,image/jpeg,image/webp,image/svg+xml"
              onChange={onPick}
              disabled={busy}
              className="hidden"
              data-testid="logo-upload-input"
            />
          </label>

          {info?.uploaded && (
            <button
              onClick={remove}
              className="ml-2 inline-flex items-center gap-2 px-5 py-3 rounded-md border border-[#B91C1C] text-[#B91C1C] font-semibold hover:bg-[#B91C1C] hover:text-white transition-colors"
              data-testid="logo-remove-btn"
            >
              <Trash2 className="w-4 h-4" />
              Remove
            </button>
          )}

          <div className="mt-6 p-3 bg-[#F4F6F5] border border-[#E3E6E4] rounded-md text-xs text-[#5a6663]">
            <strong className="text-[#0f1a14]">Tip:</strong> all signed-in
            users will see the new logo on next page interaction; force-refresh
            the browser if it appears cached.
          </div>
        </div>
      </div>
    </div>
  );
}
